import React, { useEffect, useMemo, useRef } from 'react';
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconRotateClockwise,
} from '@tabler/icons-react';
import { Slider, Text } from '@mantine/core';
import { steps, useMapContext } from '@/contexts/MapContext';
import styles from './simulationplayer.module.css';

type PlayerControlProps = {
  isFinished: boolean;
  isPlaying: boolean;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  playSimulation: () => void;
};

const PlayerControl = ({
  isFinished,
  isPlaying,
  setValue,
  setIsPlaying,
  playSimulation,
}: PlayerControlProps) => {
  if (isFinished) {
    return (
      <IconRotateClockwise
        size={18}
        onClick={() => {
          setValue(0);
          playSimulation();
        }}
      />
    );
  } else if (isPlaying) {
    return <IconPlayerPauseFilled size={18} onClick={() => setIsPlaying(false)} />;
  }

  return <IconPlayerPlayFilled size={18} onClick={playSimulation} />;
};

export const SimulationPlayer = () => {
  const {
    isSimulationPlaying,
    simulationSliderValue,
    setIsSimulationPlaying,
    setSimulationSliderValue,
    playSimulation,
    onSimulationStepChange,
  } = useMapContext();

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const marks = useMemo(() => {
    const count = steps.length;
    const stepSize = 100 / (count - 1);

    const visibleCount = Math.min(count, 6);
    const interval = Math.ceil(count / visibleCount);

    return steps.map((label, index) => ({
      value: index * stepSize,
      label: index % interval === 0 ? label : '',
    }));
  }, [steps]);

  useEffect(() => {
    if (isSimulationPlaying) {
      intervalRef.current = setInterval(() => {
        setSimulationSliderValue((prev) => {
          const newVal = prev + 100 / (steps.length - 1);
          const stepSize = 100 / (steps.length - 1);
          const index = Math.round(newVal / stepSize);
          if (index === steps.length - 1) {
            setIsSimulationPlaying(false);
          }
          return newVal;
        });
      }, 1500);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSimulationPlaying]);

  useEffect(() => {
    const stepSize = 100 / (steps.length - 1);
    const index = Math.round(simulationSliderValue / stepSize);

    if (index === steps.length - 1) {
      setIsSimulationPlaying(false);
    }

    const currentStep = steps[index];

    if (currentStep) {
      onSimulationStepChange(index);
    }
  }, [simulationSliderValue, steps]);

  const isFinished = useMemo(() => {
    const stepSize = 100 / (steps.length - 1);
    const index = Math.round(simulationSliderValue / stepSize);

    return index === steps.length - 1;
  }, [simulationSliderValue, steps]);

  const date = useMemo(() => {
    const stepSize = 100 / (steps.length - 1);
    const index = Math.round(simulationSliderValue / stepSize);

    return steps[index];
  }, [simulationSliderValue]);

  return (
    <div className={styles.player}>
      <div className={styles.dateRange}>
        <Text size="sm">Date range:</Text>
        <Text size="xs">20 Feb 25 - 27 Feb 25</Text>
      </div>
      <div>
        <Slider
          defaultValue={0}
          step={100 / (steps.length - 1)}
          marks={marks}
          classNames={{
            label: styles['slider-label'],
            root: styles['slider-root'],
            markLabel: styles['slider-markLabel'],
          }}
          showLabelOnHover={false}
          value={simulationSliderValue}
          onChange={setSimulationSliderValue}
        />
      </div>
      <div className={styles.control}>
        <PlayerControl
          isFinished={isFinished}
          isPlaying={isSimulationPlaying}
          setIsPlaying={setIsSimulationPlaying}
          setValue={setSimulationSliderValue}
          playSimulation={playSimulation}
        />
        <Text size="xs">{date}</Text>
      </div>
    </div>
  );
};
